import { FormEvent, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTheme } from "../context/ThemeContext";

const BASE = `${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/api/user-management`;

type UserForm = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  roleName: string;
  isActive: boolean;
};

type RoleOption = {
  id: number | string;
  role_name: string;
};

type UserRow = {
  id: number | string;
  first_name: string;
  last_name: string;
  email: string;
  role_name: string;
  is_active: boolean;
  created_by_email: string;
  created_at: string;
  updated_at: string;
};

type ToastState = {
  type: "success" | "error";
  message: string;
};

const EMPTY_FORM: UserForm = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  roleName: "",
  isActive: true,
};

const PAGE_SIZE = 10;

const formatDate = (value: string): string => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const roleBadgeClasses: Record<string, string> = {
  admin: "bg-indigo-50 text-indigo-700",
  "pe-user": "bg-sky-50 text-sky-700",
  guest: "bg-amber-50 text-amber-700",
};

const UserManagement = () => {
  const { isDarkMode } = useTheme();

  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [usersCount, setUsersCount] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<UserForm>(EMPTY_FORM);
  const [editingUserId, setEditingUserId] = useState("");
  const [editForm, setEditForm] = useState<UserForm>(EMPTY_FORM);
  const [deleteConfirmUserId, setDeleteConfirmUserId] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [actionInProgress, setActionInProgress] = useState("");
  const [toast, setToast] = useState<ToastState | null>(null);
  const [page, setPage] = useState(1);

  const pageCount = Math.max(1, Math.ceil(users.length / PAGE_SIZE));

  const pagedUsers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return users.slice(start, start + PAGE_SIZE);
  }, [users, page]);

  const showToast = (type: ToastState["type"], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4000);
  };

  const getApiStatus = (error: unknown): number | null => {
    const status = (error as { response?: { status?: unknown } })?.response?.status;
    return typeof status === "number" ? status : null;
  };

  const getApiDetail = (error: unknown): string | null => {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    return typeof detail === "string" ? detail : null;
  };

  const fetchRoles = async () => {
    setRolesLoading(true);

    try {
      const response = await axios.get(`${BASE}/roles?include_inactive=false`, {
        withCredentials: true,
      });
      setRoles(Array.isArray(response.data?.items) ? (response.data.items as RoleOption[]) : []);
    } catch (error) {
      setRoles([]);
      if (getApiStatus(error) === 401) {
        showToast("error", "Session expired. Please login again.");
      }
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);

    try {
      const response = await axios.get(`${BASE}/users?include_inactive=true`, {
        withCredentials: true,
      });
      const nextUsers = Array.isArray(response.data?.items)
        ? (response.data.items as UserRow[])
        : [];
      setUsers(nextUsers);
      setUsersCount(Number(response.data?.count || nextUsers.length));
      setPage(1);
    } catch (error) {
      setUsers([]);
      setUsersCount(0);
      if (getApiStatus(error) === 401) {
        showToast("error", "Session expired. Please login again.");
      } else {
        showToast("error", "Failed to load users.");
      }
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    void fetchRoles();
    void fetchUsers();
  }, []);

  const openCreate = () => {
    setCreateForm(EMPTY_FORM);
    setEditingUserId("");
    setDeleteConfirmUserId("");
    setIsCreateOpen(true);
  };

  const openEdit = (user: UserRow) => {
    setEditForm({
      firstName: user.first_name || "",
      lastName: user.last_name || "",
      email: user.email || "",
      password: "",
      roleName: user.role_name || "",
      isActive: Boolean(user.is_active),
    });
    setEditingUserId(String(user.id || ""));
    setDeleteConfirmUserId("");
    setIsCreateOpen(false);
  };

  const openDelete = (user: UserRow) => {
    setDeleteConfirmUserId(String(user.id || ""));
    setEditingUserId("");
    setIsCreateOpen(false);
  };

  const closeCreatePanel = () => setIsCreateOpen(false);

  const cancelInlineEdit = () => {
    setEditingUserId("");
    setEditForm(EMPTY_FORM);
  };

  const cancelInlineDelete = () => {
    setDeleteConfirmUserId("");
  };

  const handleCreateFormChange = <K extends keyof UserForm>(field: K, value: UserForm[K]) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditFormChange = <K extends keyof UserForm>(field: K, value: UserForm[K]) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!createForm.password || createForm.password.length < 8) {
      showToast("error", "Password must be at least 8 characters long.");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(
        `${BASE}/users`,
        {
          first_name: createForm.firstName,
          last_name: createForm.lastName,
          email: createForm.email,
          password: createForm.password,
          role_name: createForm.roleName,
          is_active: createForm.isActive,
        },
        {
          withCredentials: true,
        },
      );

      showToast("success", "User created successfully.");
      setCreateForm(EMPTY_FORM);
      setIsCreateOpen(false);
      await fetchUsers();
    } catch (error) {
      showToast("error", getApiDetail(error) || "Failed to create user.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInlineUpdate = async (userId: number | string) => {
    const normalizedUserId = String(userId).trim();
    if (!normalizedUserId) {
      showToast("error", "Invalid user ID.");
      return;
    }

    if (editForm.password && editForm.password.length < 8) {
      showToast("error", "Password must be at least 8 characters long.");
      return;
    }

    setActionInProgress(`edit-${normalizedUserId}`);

    try {
      await axios.put(
        `${BASE}/users/${normalizedUserId}`,
        {
          first_name: editForm.firstName,
          last_name: editForm.lastName,
          email: editForm.email,
          password: editForm.password || undefined,
          role_name: editForm.roleName,
          is_active: editForm.isActive,
        },
        {
          withCredentials: true,
        },
      );

      showToast("success", "User updated successfully.");
      setEditingUserId("");
      setEditForm(EMPTY_FORM);
      await fetchUsers();
    } catch (error) {
      showToast("error", getApiDetail(error) || "Failed to update user.");
    } finally {
      setActionInProgress("");
    }
  };

  const handleInlineDelete = async (userId: number | string) => {
    const normalizedUserId = String(userId).trim();
    if (!normalizedUserId) {
      showToast("error", "Invalid user ID.");
      return;
    }

    setActionInProgress(`delete-${normalizedUserId}`);

    try {
      await axios.delete(`${BASE}/users/${normalizedUserId}?hard_delete=true`, {
        withCredentials: true,
      });
      showToast("success", "User deleted successfully.");
      setDeleteConfirmUserId("");
      await fetchUsers();
    } catch (error) {
      showToast("error", getApiDetail(error) || "Failed to delete user.");
    } finally {
      setActionInProgress("");
    }
  };

  const inputClass =
    "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-lime-400 focus:bg-white focus:ring-2 focus:ring-lime-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-lime-500";
  const labelClass =
    "mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300";

  const renderCreateForm = () => (
    <form onSubmit={handleCreate} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={createForm.firstName}
            onChange={(e) => handleCreateFormChange("firstName", e.target.value)}
            placeholder="Enter first name"
            required
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={createForm.lastName}
            onChange={(e) => handleCreateFormChange("lastName", e.target.value)}
            placeholder="Enter last name"
            required
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>
          Email ID <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          value={createForm.email}
          onChange={(e) => handleCreateFormChange("email", e.target.value)}
          placeholder="Enter email address"
          required
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>
          Password <span className="text-red-500">*</span>
        </label>
        <input
          type="password"
          value={createForm.password}
          onChange={(e) => handleCreateFormChange("password", e.target.value)}
          placeholder="Enter password (min 8 characters)"
          required
          className={inputClass}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Password must be at least 8 characters long.
        </p>
      </div>

      <div>
        <label className={labelClass}>
          Role Name <span className="text-red-500">*</span>
        </label>
        <select
          value={createForm.roleName}
          onChange={(e) => handleCreateFormChange("roleName", e.target.value)}
          required
          disabled={rolesLoading}
          className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <option value="">{rolesLoading ? "Loading roles..." : "-- Select Role --"}</option>
          {roles.map((role) => (
            <option key={role.id} value={role.role_name}>
              {role.role_name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={createForm.isActive}
          onClick={() => handleCreateFormChange("isActive", !createForm.isActive)}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-lime-400 focus:ring-offset-2 ${
            createForm.isActive ? "bg-lime-500" : "bg-gray-300 dark:bg-gray-600"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              createForm.isActive ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Is Active:{" "}
          <span className={createForm.isActive ? "text-lime-600" : "text-gray-400"}>
            {createForm.isActive ? "Yes" : "No"}
          </span>
        </span>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center rounded-xl bg-lime-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-lime-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Saving..." : "Create User"}
        </button>
        <button
          type="button"
          onClick={closeCreatePanel}
          className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div
      className={`${
        isDarkMode ? "bg-black" : "bg-[#f4fbf6]"
      } min-h-screen w-full relative overflow-hidden`}
    >
      {!isDarkMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -left-10 h-56 w-56 rounded-full bg-emerald-100/70 blur-3xl" />
          <div className="absolute top-20 right-10 h-48 w-48 rounded-full bg-sky-100/70 blur-3xl" />
          <div className="absolute bottom-8 left-1/3 h-44 w-44 rounded-full bg-lime-100/60 blur-3xl" />
        </div>
      )}

      <div className="relative z-10 mx-auto max-w-7xl px-6 py-10">
        <div className="text-center pb-6">
          <span className="inline-flex items-center rounded-full border border-lime-300/80 bg-lime-50 px-4 py-1 text-xs font-bold uppercase tracking-wide text-lime-800 dark:border-lime-700 dark:bg-lime-900/40 dark:text-lime-200">
            Admin Panel
          </span>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white">
            User Management
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            List of users and quick actions to create, update, and delete.
          </p>
        </div>

        {toast && (
          <div
            className={`mb-6 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold shadow ${
              toast.type === "success"
                ? "bg-lime-50 border border-lime-300 text-lime-800 dark:bg-lime-900/30 dark:border-lime-700 dark:text-lime-200"
                : "bg-red-50 border border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-200"
            }`}
          >
            <span>{toast.type === "success" ? "✓" : "✕"}</span>
            <span>{toast.message}</span>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Total Users: {usersCount}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchUsers}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="rounded-lg bg-lime-600 px-3 py-2 text-xs font-semibold text-white hover:bg-lime-700"
            >
              + Create User
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-[#dbe7f3] bg-white shadow-[0_10px_24px_rgba(16,24,40,0.08)] dark:border-gray-800 dark:bg-gray-950">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#1f3550] text-white">
                <tr>
                  <th className="px-4 py-3 font-semibold">User ID</th>
                  <th className="px-4 py-3 font-semibold">Full Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Created By</th>
                  <th className="px-4 py-3 font-semibold">Joined Date</th>
                  <th className="px-4 py-3 font-semibold">Last Active</th>
                  <th className="px-4 py-3 font-semibold text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersLoading && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      Loading users...
                    </td>
                  </tr>
                )}

                {!usersLoading && pagedUsers.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      No users found.
                    </td>
                  </tr>
                )}

                {!usersLoading &&
                  pagedUsers.map((user, index) => {
                    const roleKey = String(user.role_name || "").toLowerCase();
                    const roleClass = roleBadgeClasses[roleKey] || "bg-gray-100 text-gray-700";
                    const normalizedUserId = String(user.id);
                    const isEditing = editingUserId === normalizedUserId;
                    const isDeleteConfirm = deleteConfirmUserId === normalizedUserId;
                    const serialUserId = (page - 1) * PAGE_SIZE + index + 1;

                    return (
                      <tr
                        key={normalizedUserId}
                        className="border-b border-[#edf3f9] text-[#5b7293] dark:border-gray-800 dark:text-gray-200"
                      >
                        <td className="px-4 py-3 font-semibold">{serialUserId}</td>
                        <td className="px-4 py-3 font-semibold text-[#223b5a] dark:text-white">
                          {isEditing ? (
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                              <input
                                value={editForm.firstName}
                                onChange={(e) => handleEditFormChange("firstName", e.target.value)}
                                className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-900"
                              />
                              <input
                                value={editForm.lastName}
                                onChange={(e) => handleEditFormChange("lastName", e.target.value)}
                                className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-900"
                              />
                            </div>
                          ) : (
                            `${user.first_name || ""} ${user.last_name || ""}`.trim() || "-"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <input
                              type="email"
                              value={editForm.email}
                              onChange={(e) => handleEditFormChange("email", e.target.value)}
                              className="w-full rounded border border-gray-300 px-2 py-1 text-xs text-gray-900"
                            />
                          ) : (
                            user.email || "-"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <button
                              type="button"
                              onClick={() => handleEditFormChange("isActive", !editForm.isActive)}
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                                editForm.isActive
                                  ? "bg-lime-100 text-lime-700"
                                  : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              {editForm.isActive ? "Active" : "Inactive"}
                            </button>
                          ) : (
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                                user.is_active
                                  ? "bg-lime-100 text-lime-700"
                                  : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              {user.is_active ? "Active" : "Inactive"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <select
                              value={editForm.roleName}
                              onChange={(e) => handleEditFormChange("roleName", e.target.value)}
                              className="w-full rounded border border-gray-300 px-2 py-1 text-xs text-gray-900"
                              disabled={rolesLoading}
                            >
                              {roles.map((role) => (
                                <option key={role.id} value={role.role_name}>
                                  {role.role_name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${roleClass}`}
                            >
                              {user.role_name || "-"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">{user.created_by_email || "-"}</td>
                        <td className="px-4 py-3">{formatDate(user.created_at)}</td>
                        <td className="px-4 py-3">{formatDate(user.updated_at)}</td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleInlineUpdate(user.id)}
                                disabled={actionInProgress === `edit-${normalizedUserId}`}
                                className="rounded bg-lime-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-60"
                              >
                                {actionInProgress === `edit-${normalizedUserId}`
                                  ? "Saving"
                                  : "Save"}
                              </button>
                              <button
                                type="button"
                                onClick={cancelInlineEdit}
                                className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : isDeleteConfirm ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleInlineDelete(user.id)}
                                disabled={actionInProgress === `delete-${normalizedUserId}`}
                                className="rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-60"
                              >
                                {actionInProgress === `delete-${normalizedUserId}`
                                  ? "Deleting"
                                  : "Confirm"}
                              </button>
                              <button
                                type="button"
                                onClick={cancelInlineDelete}
                                className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(user)}
                                className="rounded p-1 text-sky-600 hover:bg-sky-50"
                                title="Update user"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => openDelete(user)}
                                className="rounded p-1 text-red-600 hover:bg-red-50"
                                title="Delete user"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-[#edf3f9] px-4 py-3 text-sm text-gray-600 dark:border-gray-800 dark:text-gray-300">
            <span>
              Rows per page <strong>{PAGE_SIZE}</strong>
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold disabled:opacity-40"
              >
                Prev
              </button>
              {Array.from({ length: pageCount }, (_, index) => index + 1)
                .slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5)
                .map((pageNo) => (
                  <button
                    key={pageNo}
                    type="button"
                    onClick={() => setPage(pageNo)}
                    className={`h-8 w-8 rounded-full text-xs font-bold ${
                      pageNo === page
                        ? "bg-[#1f3550] text-white"
                        : "border border-gray-300 text-gray-700"
                    }`}
                  >
                    {pageNo}
                  </button>
                ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page === pageCount}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {isCreateOpen && (
          <div className="mt-6 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-[0_10px_26px_rgba(16,24,40,0.08)] p-6">
            <div className="mb-5 flex items-center gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
              <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500" />
              <span>Create New User</span>
            </div>
            {renderCreateForm()}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;